/**
 * The assistant's session on the AI gateway's hosted agent (`/v1/agent`, ml-services llm-gateway AGENT.md): the
 * same protocol Studio's Sidekick speaks. The gateway runs the loop, the prompt, the specialists and its hosted
 * tools; this editor declares the tools it can run, answers each `tool_request` with them, and shows the turn.
 *
 *   POST /v1/agent/sessions                  open, declaring the toolset
 *   POST /v1/agent/sessions/:id/turns        a turn; answers its event stream
 *   POST /v1/agent/sessions/:id/tool-results answer a tool_request
 *   POST /v1/agent/sessions/:id/attach       follow a parked or dropped turn, or re-declare the toolset
 *   POST /v1/agent/sessions/:id/cancel       stop the turn
 *
 * No bundle of this editor holds a prompt, a provider SDK or a loop. The bench drives the same class.
 */

import { parseRetryAfter, waitForRateLimit, whileRateLimited } from './rate-limit-wait';

export interface DeclaredTool {
    name: string;
    inputSchema: Record<string, unknown>;
}

export interface SceneState {
    outline: string;
    selection?: unknown;
    /** The scene's exact state as an id (scene-revision.ts): the gateway's review_scene tells a current screenshot by it. */
    revision?: string;
}

/** An MCP tool result, as `tool-results` takes it. */
export interface ToolAnswer {
    content: Record<string, unknown>[];
    structuredContent?: unknown;
    isError?: boolean;
}

export interface ToolRequest {
    callId: string;
    tool: string;
    input: Record<string, unknown>;
    /** `context`: the gateway asks what a specialized call's specialist needs, not for the call to run. */
    stage?: 'context';
}

export type StopReason = 'end' | 'cancelled' | 'iteration_limit' | 'time_limit' | 'provider_incomplete' | 'error';

export interface TurnUsage {
    inputTokens: number;
    outputTokens: number;
    cacheReadTokens: number;
    microcredits?: number;
}

export interface TurnDone {
    stopReason: StopReason;
    usage: TurnUsage;
    error?: { code: string; message: string };
}

export interface TurnHandlers {
    /** Model text as it streams. */
    onText(delta: string): void;
    /** A tool starting, progressing or ending, as the panel shows it. */
    onToolStatus(tool: string, status: 'running' | 'done' | 'error', input?: Record<string, unknown>, progress?: string): void;
    /** Run one request: the editor's tool, or a specialized call's context. */
    runTool(request: ToolRequest): Promise<ToolAnswer>;
    /** The scene as the model should see it now. */
    scene(): SceneState;
    /** A tool the gateway ran itself (read_guide, search_catalog), with the start of its result; the bench logs these. */
    onHostedTool?(tool: string, isError: boolean, summary: string): void;
}

export interface SessionConfig {
    /** Base URL of the AI gateway, no trailing slash. */
    base: string;
    /** Fresh credentials for each request: the bearer and the billing team. */
    headers(): Promise<Record<string, string>>;
    clientKind: 'sandbox' | 'bench';
    profile?: string;
}

/** A gateway refusal, in its own words. */
export class SessionError extends Error {
    constructor(readonly status: number, readonly code: string, message: string) {
        super(message);
    }
}

interface Frame { event: string; id?: number; data: Record<string, unknown> }

/** Read an SSE body frame by frame. */
async function* frames(body: ReadableStream<Uint8Array>): AsyncGenerator<Frame> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    try {
        for (;;) {
            const { done, value } = await reader.read();
            if (done) return;
            buffer += decoder.decode(value, { stream: true });
            let end;
            while ((end = buffer.indexOf('\n\n')) >= 0) {
                const block = buffer.slice(0, end);
                buffer = buffer.slice(end + 2);
                const event = /^event: (.*)$/m.exec(block)?.[1];
                const data = /^data: (.*)$/m.exec(block)?.[1];
                if (!event || data === undefined) continue;
                const id = /^id: (.*)$/m.exec(block)?.[1];
                yield { event, data: JSON.parse(data) as Record<string, unknown>, ...(id ? { id: Number(id) } : {}) };
            }
        }
    } finally {
        reader.releaseLock();
    }
}

const sameToolset = (a: DeclaredTool[], b: DeclaredTool[]): boolean => JSON.stringify(a) === JSON.stringify(b);

/** How many times a tool's answer is sent before the turn fails, and the wait before each retry (times the attempt). */
const ANSWER_ATTEMPTS = 3;
const ANSWER_RETRY_MS = 500;

export class HostedSession {
    private sessionId: string | null = null;
    private declared: DeclaredTool[] = [];
    private lastSeq = 0;
    /** Requests already answered, so a resent one (after a resume) is not run twice. */
    private readonly answered = new Set<string>();

    constructor(private readonly config: SessionConfig) {}

    /** Forget the conversation: the next turn opens a new session. */
    reset(): void {
        this.sessionId = null;
        this.lastSeq = 0;
        this.answered.clear();
    }

    /** A request; a 429 (the gateway's per-user window) is waited out first (rate-limit-wait.ts). `signal` stops both. */
    private async post(path: string, body: unknown, signal?: AbortSignal): Promise<Response> {
        return whileRateLimited(async () => fetch(`${this.config.base}/v1/agent${path}`, {
            method: 'POST',
            headers: { ...await this.config.headers(), 'content-type': 'application/json' },
            body: JSON.stringify(body),
            ...(signal ? { signal } : {}),
        }), outcome => ('value' in outcome && outcome.value.status === 429 ? parseRetryAfter(outcome.value.headers.get('retry-after')) : undefined), signal);
    }

    private async refusal(res: Response): Promise<SessionError> {
        const body = await res.json().catch(() => ({})) as { error?: string; message?: string };
        return new SessionError(res.status, body.error ?? 'http_error', body.message ?? `The AI gateway answered ${res.status}.`);
    }

    private async open(toolset: DeclaredTool[], signal?: AbortSignal): Promise<string> {
        const res = await this.post('/sessions', { profile: this.config.profile ?? 'editor', client: { kind: this.config.clientKind }, toolset }, signal);
        if (res.status !== 201) throw await this.refusal(res);
        const { sessionId } = await res.json() as { sessionId: string };
        this.sessionId = sessionId;
        this.declared = toolset;
        this.lastSeq = 0;
        // Call ids are per session: the new one's must not be taken for answered ones of the old.
        this.answered.clear();
        return sessionId;
    }

    /** The session to run a turn in, with this toolset declared: a new one, or the current one told of a change. */
    private async ready(toolset: DeclaredTool[], signal?: AbortSignal): Promise<string> {
        if (!this.sessionId) return this.open(toolset, signal);
        if (sameToolset(toolset, this.declared)) return this.sessionId;
        const res = await this.post(`/sessions/${this.sessionId}/attach`, { after: this.lastSeq, toolset }, signal);
        if (res.status === 404 || res.status === 410) return this.open(toolset, signal);
        if (!res.ok) throw await this.refusal(res);
        await res.body?.cancel();
        this.declared = toolset;
        return this.sessionId;
    }

    /**
     * Run one turn to its end. A stream that ends without `turn_done` (the turn parked, or the connection dropped)
     * is followed again with `attach`, which resumes it where it stood. `signal` cancels the turn. A session still
     * holding an earlier turn (one lost with its tab, or parked) has it cancelled, and this message goes once more.
     */
    async turn(
        message: string, toolset: DeclaredTool[], handlers: TurnHandlers,
        /**
         * `onSent`: the gateway took the message, so it is in the conversation whatever happens to the turn after.
         * `history`: the conversation so far as quoted text, sent ahead of the message when the gateway's session
         * expired and a new one starts with none of it.
         */
        opts: { attachments?: string[]; signal?: AbortSignal; onSent?: () => void; history?: () => string | null } = {},
    ): Promise<TurnDone> {
        const before = this.sessionId;
        let sessionId = await this.ready(toolset, opts.signal);
        const bodyFor = (reopened: boolean) => {
            const history = reopened ? opts.history?.() : null;
            return { message: history ? `${history}\n\n${message}` : message, scene: handlers.scene(), ...(opts.attachments?.length ? { attachments: opts.attachments } : {}) };
        };
        let body = bodyFor(before !== null && sessionId !== before);
        // Listening before the first `turns` request: a turn the gateway started before an abort landed is cancelled too.
        const cancel = (): void => { void this.post(`/sessions/${sessionId}/cancel`, {}).catch(() => undefined); };
        const writing = new Set<string>();
        opts.signal?.addEventListener('abort', cancel, { once: true });
        try {
            let res = await this.post(`/sessions/${sessionId}/turns`, body, opts.signal);
            // An expired or closed session starts a new conversation rather than failing the turn.
            if (res.status === 404 || res.status === 410) {
                sessionId = await this.open(toolset, opts.signal);
                body = bodyFor(true);
                res = await this.post(`/sessions/${sessionId}/turns`, body, opts.signal);
            }
            if (res.status === 409) {
                await res.body?.cancel();
                const cancelled = await this.post(`/sessions/${sessionId}/cancel`, {}, opts.signal);
                await cancelled.body?.cancel();
                res = await this.post(`/sessions/${sessionId}/turns`, body, opts.signal);
            }
            if (!res.ok || !res.body) throw await this.refusal(res);
            opts.onSent?.();
            for (let attempt = 0; ; attempt++) {
                const done = await this.follow(sessionId, res.body, handlers, writing, opts.signal);
                if (done) return done;
                if (attempt >= 5) throw new SessionError(0, 'turn_lost', 'The AI gateway stopped answering this turn.');
                res = await this.post(`/sessions/${sessionId}/attach`, { after: this.lastSeq, scene: handlers.scene() }, opts.signal);
                if (!res.ok || !res.body) throw await this.refusal(res);
            }
        } finally {
            opts.signal?.removeEventListener('abort', cancel);
            // The turn ended between a writing and its build (a cancel, an error): no chip stays "writing code…".
            for (const name of writing) handlers.onToolStatus(name, 'error');
        }
    }

    /**
     * Send a tool's answer. The tool ran once and must not run again, so a lost post (the network, or the gateway's
     * 5xx) is sent again, a few times, rather than left to a resume. 409: the first answer stands, which is this one.
     */
    private async postAnswer(sessionId: string, body: { callId: string; result: ToolAnswer; scene: SceneState }, signal?: AbortSignal): Promise<void> {
        for (let attempt = 1; ; attempt++) {
            let posted: Response | null = null;
            try {
                posted = await this.post(`/sessions/${sessionId}/tool-results`, body, signal);
            } catch (err) {
                if (signal?.aborted || attempt >= ANSWER_ATTEMPTS) throw err;
            }
            if (posted && (posted.ok || posted.status === 409)) return;
            if (posted && (posted.status < 500 || attempt >= ANSWER_ATTEMPTS)) throw await this.refusal(posted);
            await waitForRateLimit(ANSWER_RETRY_MS * attempt, signal);
        }
    }

    /**
     * Read one stream: show text and tools, answer requests. Answers the turn's end, or null when the stream ended
     * first. `writing`: tools whose code a specialist is writing, each shown as one chip from the writing to the build.
     */
    private async follow(sessionId: string, body: ReadableStream<Uint8Array>, handlers: TurnHandlers, writing: Set<string>, signal?: AbortSignal): Promise<TurnDone | null> {
        for await (const frame of frames(body)) {
            if (frame.id !== undefined) this.lastSeq = Math.max(this.lastSeq, frame.id);
            const data = frame.data;
            switch (frame.event) {
                case 'text_delta':
                    handlers.onText(typeof data.text === 'string' ? data.text : '');
                    break;
                case 'hosted_tool': {
                    const name = String(data.name);
                    const status = data.status as 'running' | 'done' | 'error';
                    if (status === 'running') {
                        if (writing.has(name)) break;
                        if (data.summary) writing.add(name);
                        handlers.onToolStatus(name, 'running', undefined, data.summary ? 'writing code…' : undefined);
                    } else if (status === 'error' || !writing.has(name)) {
                        if (!writing.delete(name)) handlers.onHostedTool?.(name, status === 'error', typeof data.summary === 'string' ? data.summary : '');
                        handlers.onToolStatus(name, status);
                    }
                    break;
                }
                case 'tool_request': {
                    const request = { callId: String(data.callId), tool: String(data.tool), input: (data.input ?? {}) as Record<string, unknown>, ...(data.stage === 'context' ? { stage: 'context' as const } : {}) };
                    if (this.answered.has(request.callId)) break;
                    this.answered.add(request.callId);
                    if (!request.stage) {
                        if (writing.delete(request.tool)) handlers.onToolStatus(request.tool, 'running', request.input, 'building…');
                        else handlers.onToolStatus(request.tool, 'running', request.input);
                    }
                    let answer: ToolAnswer;
                    try {
                        answer = await handlers.runTool(request);
                    } catch (err) {
                        answer = { content: [{ type: 'text', text: `Error: ${err instanceof Error ? err.message : String(err)}` }], isError: true };
                    }
                    if (!request.stage) handlers.onToolStatus(request.tool, answer.isError ? 'error' : 'done', request.input);
                    await this.postAnswer(sessionId, { callId: request.callId, result: answer, scene: handlers.scene() }, signal);
                    break;
                }
                case 'turn_done':
                    return { stopReason: data.stopReason as StopReason, usage: data.usage as TurnUsage, ...(data.error ? { error: data.error as TurnDone['error'] } : {}) };
                default:
                    break;
            }
        }
        return null;
    }
}
